/**
 * ModulesPage — Module list grid
 *
 * Displays all 8 modules with progress state, primary character
 * avatar, and character colour accents.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, PlayCircle, Circle } from 'lucide-react';

import { useProgress } from '../context/ProgressContext';
import { getCharacterForModule } from '../data/characters';
import CharacterAvatar from '../components/characters/CharacterAvatar';
import Tondo from '../components/ornament/Tondo';
import Card from '../components/shared/Card';
import { MODULE_METADATA } from '../data/modules/index';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4 },
  }),
};

const statusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-courtyard-olive-dark" aria-hidden="true" />;
    case 'started':
      return <PlayCircle className="w-5 h-5 text-courtyard-gold-ink" aria-hidden="true" />;
    default:
      return <Circle className="w-5 h-5 text-courtyard-stone/80" aria-hidden="true" />;
  }
};

const statusLabel = (status) => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'started':
      return 'In Progress';
    default:
      return 'Not Started';
  }
};

const ModulesPage = () => {
  const { moduleProgress } = useProgress();

  return (
    <>
      <Helmet>
        <title>The Eight Evenings | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-10">
        <motion.div
          className="text-center mb-8 rounded-2xl bg-gradient-to-br from-philemon-bg/40 via-courtyard-parchment to-theron-bg/30 border border-courtyard-olive/15 px-6 py-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h1 className="font-display text-3xl md:text-4xl font-bold text-courtyard-stone">
            The Eight Evenings
          </h1>
          <p className="mt-2 text-courtyard-stone/80">One story, told over eight evenings in Xenia&rsquo;s courtyard. Start with the first.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {MODULE_METADATA.map((mod, i) => {
            const charInfo = getCharacterForModule(mod.id);
            const primaryChar = charInfo?.primary;
            const status = moduleProgress[mod.id]?.status || 'not_started';

            return (
              <motion.div
                key={mod.id}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i}
              >
                <Link to={`/evenings/${mod.id}`}>
                  <Card
                    variant={primaryChar?.id || 'default'}
                    className={`p-5 h-full hover:shadow-courtyard-md transition-all hover:-translate-y-0.5 border-l-4 ${
                      primaryChar?.id === 'philemon' ? 'border-l-courtyard-olive' :
                      primaryChar?.id === 'xenia' ? 'border-l-courtyard-aegean' :
                      primaryChar?.id === 'theron' ? 'border-l-courtyard-terracotta' :
                      'border-l-courtyard-gold'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <Tondo evening={mod.id} size={64} className="flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <CharacterAvatar characterId={primaryChar?.id} size="xs" />
                          <span className="text-sm font-bold text-courtyard-stone/80">
                            Evening {mod.id}
                          </span>
                          {statusIcon(status)}
                        </div>
                        <h3 className="font-display text-lg font-bold text-courtyard-stone leading-tight">
                          {mod.title}
                        </h3>
                        <p className="text-sm text-courtyard-stone/80 mt-1 leading-relaxed">
                          {mod.subtitle}
                        </p>
                        <p className="text-sm text-courtyard-stone/80 mt-2 font-medium">
                          {statusLabel(status)}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ModulesPage;
